/**
 * Rubrique Matériel (plan validé du 17/10/2026) : le stock et les achats sous
 * un seul nom. Un accueil qui oriente (`/materiel`) et une liste par sujet, à
 * son adresse. Une seule source pour la rubrique (`areas.ts`), ses pages
 * (`features/stock`, `features/suppliers`) et les anciennes adresses
 * (`/stock?onglet=…`, `/fournisseurs?tab=…`) encore portées par des avis, des
 * e-mails et des signets. Les droits sont ceux que l'API vérifie
 * (`apps/api/src/domains/purchasing/access.ts`).
 */
import type { Capability } from '@myequip/shared';

export const MATERIEL_PATHS = {
  accueil: '/materiel',
  stock: '/materiel/stock',
  articles: '/materiel/articles',
  emplacements: '/materiel/emplacements',
  surveiller: '/materiel/a-surveiller',
  inventaires: '/materiel/inventaires',
  commandes: '/materiel/commandes',
  fournisseurs: '/materiel/fournisseurs',
  factures: '/materiel/factures',
  aCommander: '/materiel/a-commander',
} as const;

export type MaterielSection = Exclude<keyof typeof MATERIEL_PATHS, 'accueil'>;

/** Lire les commandes : les demander, les approuver, gérer ou recevoir le stock. */
export const ORDER_READERS: readonly Capability[] = [
  'purchase.propose', 'purchase.approve', 'stock.manage', 'stock.consume_transfer',
];
/** Lire le carnet des fournisseurs : commander, gérer le stock, saisir une dépense. */
export const SUPPLIER_READERS: readonly Capability[] = [
  'purchase.propose', 'purchase.approve', 'stock.manage', 'expense.record', 'expense.manage',
];
/** Lire les factures reçues : les siennes, toutes, ou celles lues automatiquement (stock). */
export const INVOICE_READERS: readonly Capability[] = ['expense.record', 'expense.manage', 'stock.manage'];
/** Saisir une facture reçue ou une dépense. */
export const INVOICE_WRITERS: readonly Capability[] = ['expense.record', 'expense.manage'];
/** Voir la rubrique au bureau : un seul de ces droits suffit. */
export const MATERIEL_ANY: readonly Capability[] = [
  'stock.manage', 'stock.consume_transfer', 'purchase.propose', 'purchase.approve', 'expense.manage', 'expense.record',
];

type Can = (capability: Capability) => boolean;
const any = (can: Can, capabilities: readonly Capability[]): boolean => capabilities.some(can);

/** Les besoins à commander : le bureau qui regroupe les commandes (mêmes droits que l'API). */
export function ordersNeeds(can: Can): boolean {
  return can('purchase.propose') && can('planning.edit_all');
}

/** Les listes de la rubrique que ce compte peut ouvrir, dans l'ordre de l'accueil. */
export function materielSections(can: Can): MaterielSection[] {
  const sections: MaterielSection[] = ['stock', 'articles', 'emplacements', 'surveiller'];
  // Inventaires (décision 15) : chaque étape demande la gestion du stock, comme l'API.
  if (can('stock.manage')) sections.push('inventaires');
  if (any(can, ORDER_READERS)) sections.push('commandes');
  if (any(can, SUPPLIER_READERS)) sections.push('fournisseurs');
  if (any(can, INVOICE_READERS)) sections.push('factures');
  if (ordersNeeds(can)) sections.push('aCommander');
  return sections;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Anciens noms de paramètres, lus une fois pour réécrire l'adresse sous les
 * noms communs (docs/agents/UI.md §4) : l'emplacement affiché devient `vue`,
 * l'onglet des achats et son filtre deviennent une liste et son `etat`.
 */
const LEGACY = { tab: 'tab', filtre: 'filtre', emplacement: 'emplacement', commande: 'orderId' } as const;

/** « Ce qu'il y a où », pour un seul emplacement. */
export function stockAt(locationId: string): string {
  return `${MATERIEL_PATHS.stock}?vue=${encodeURIComponent(locationId)}`;
}

/**
 * Ancienne adresse `/stock?onglet=…&emplacement=…&filtre=…` : la liste qui
 * porte désormais ce contenu. Les anciens onglets (réservations, écarts,
 * réassort) mènent à « À surveiller ».
 */
export function legacyStockPath(search: string): string {
  const params = new URLSearchParams(search);
  switch (params.get('onglet')) {
    case 'articles':
      return params.get(LEGACY.filtre) === 'sous-seuil' ? `${MATERIEL_PATHS.articles}?etat=sous-seuil` : MATERIEL_PATHS.articles;
    case 'emplacements':
    case 'vehicules':
      return MATERIEL_PATHS.emplacements;
    case 'a-surveiller':
    case 'reservations':
    case 'anomalies':
    case 'reassort':
      return MATERIEL_PATHS.surveiller;
    default: {
      const place = params.get(LEGACY.emplacement) ?? params.get('vue');
      return place && UUID.test(place) ? stockAt(place) : MATERIEL_PATHS.stock;
    }
  }
}

/**
 * Ancienne adresse `/fournisseurs?tab=…&filtre=…&orderId=…` : la commande
 * citée, sinon la liste de l'onglet, sinon la première liste d'achats permise.
 */
export function legacySuppliersPath(search: string, can: Can): string {
  const params = new URLSearchParams(search);
  const order = params.get(LEGACY.commande);
  if (order && UUID.test(order)) return `/fournisseurs/commandes/${order}`;
  switch (params.get(LEGACY.tab)) {
    case 'commandes':
      return params.get(LEGACY.filtre) === 'a-receptionner'
        ? `${MATERIEL_PATHS.commandes}?etat=a_receptionner`
        : MATERIEL_PATHS.commandes;
    case 'fournisseurs':
      return MATERIEL_PATHS.fournisseurs;
    case 'factures':
    case 'ocr':
      return MATERIEL_PATHS.factures;
    case 'a-commander':
      return MATERIEL_PATHS.aCommander;
    default:
      // Sans onglet : les commandes pour qui commande, sinon les factures reçues.
      if (any(can, ORDER_READERS)) return MATERIEL_PATHS.commandes;
      if (any(can, INVOICE_READERS)) return MATERIEL_PATHS.factures;
      return MATERIEL_PATHS.accueil;
  }
}
